import { useState } from "react";
import { toast } from "sonner";
import { Ban, Undo2, Truck, Repeat } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";

const CANCEL_REASON = {
  restaurant: "Asta annullata dal ristorante",
  unpaid: "Ordine annullato: pagamento non completato entro 48 ore dall'aggiudicazione",
  admin: "Ordine annullato dall'amministratore",
  mancata_consegna: "Ordine annullato per mancata consegna",
};

function ReasonDialog({ open, onOpenChange, title, description, label, placeholder, minLength, confirmLabel, danger, onConfirm, testId }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const tooShort = reason.trim().length < minLength;
  const submit = async () => {
    setBusy(true);
    try {
      await onConfirm(reason.trim());
      setReason("");
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={`${testId}-dialog`}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        <div>
          <Label htmlFor={`${testId}-reason`}>{label}{minLength > 0 && <span className="text-slate-400 text-xs"> (min. {minLength} caratteri)</span>}</Label>
          <Textarea id={`${testId}-reason`} rows={3} maxLength={500} value={reason} onChange={e => setReason(e.target.value)} placeholder={placeholder} data-testid={`${testId}-reason`} />
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-full" onClick={() => onOpenChange(false)}>Indietro</Button>
          <Button onClick={submit} disabled={busy || tooShort} className={`rounded-full ${danger ? "bg-red-600 hover:bg-red-700 text-white" : "ga-btn-primary"}`} data-testid={`${testId}-confirm`}>
            {busy ? "Invio..." : confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function OrderLifecyclePanel({ order, isRestOwner, isWinner, onReload, onLeave }) {
  const [dialog, setDialog] = useState(null);
  const [busy, setBusy] = useState(false);
  const close = (open) => { if (!open) setDialog(null); };
  const post = async (path, body, message, leave = false) => {
    await api.post(`/orders/${order.id}${path}`, body);
    toast.success(message);
    if (leave) onLeave(); else onReload();
  };
  const respond = async (accept) => {
    setBusy(true);
    try { await post(`/reassignment/${accept ? "accept" : "decline"}`, undefined, accept ? "Ordine accettato: ora sei il fornitore" : "Proposta rifiutata", !accept); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); setDialog(null); }
  };
  const canWithdraw = isWinner && order.status === "awarded" && !order.payment_pending && order.confirmation_status !== "disputed";
  const withdrawAfterPayment = isWinner && (order.status === "paid" || (order.status === "awarded" && order.payment_pending)) && order.confirmation_status !== "disputed";

  return (
    <>
      {order.status === "cancelled" && (
        <div className="ga-card p-4 border-2 border-slate-300 bg-slate-50" data-testid="order-cancelled-banner">
          <div className="text-xs font-mono uppercase tracking-widest text-slate-500 flex items-center gap-1.5"><Ban className="w-3.5 h-3.5" /> Annullato{order.cancelled_at ? ` il ${new Date(order.cancelled_at).toLocaleString("it-IT")}` : ""}</div>
          <div className="mt-1 text-sm font-semibold text-slate-900">{CANCEL_REASON[order.cancel_reason] || "Ordine annullato"}</div>
          {order.cancel_note && order.cancel_reason !== "unpaid" && <div className="mt-1 text-xs text-slate-600">Motivo: "{order.cancel_note}"</div>}
        </div>
      )}

      {isRestOwner && order.status === "reassigning" && (
        <div className="ga-card p-4 border-2 border-amber-300 bg-amber-50/60" data-testid="order-reassigning-banner">
          <div className="text-xs font-mono uppercase tracking-widest text-amber-700 flex items-center gap-1.5"><Repeat className="w-3.5 h-3.5" /> In riassegnazione</div>
          <div className="mt-1 text-sm text-slate-900">Il fornitore ha rinunciato all'ordine. Lo proponiamo alla prossima offerta migliore al prezzo che aveva offerto: ti avvisiamo appena viene accettato. Se nessuno accetta, l'ordine viene annullato.</div>
        </div>
      )}

      {order.proposal && (
        <div className="ga-card p-5 border-2 border-emerald-300 bg-emerald-50/40" data-testid="reassignment-proposal-card">
          <div className="text-xs font-mono uppercase tracking-widest text-emerald-700">Ordine proposto a te</div>
          <div className="mt-1 font-display text-2xl font-bold text-slate-900">€ {Number(order.proposal.price || 0).toFixed(2)}</div>
          <div className="text-xs text-slate-600 mt-1">Il fornitore vincitore ha rinunciato. Puoi accettare l'ordine al prezzo che avevi offerto{order.proposal.delivery_time_hours ? `, con consegna in ${order.proposal.delivery_time_hours}h` : ""}. I dati dell'acquirente diventano visibili dopo l'accettazione.</div>
          <div className="mt-3 flex gap-2 flex-wrap">
            <Button onClick={() => respond(true)} disabled={busy} className="ga-btn-primary rounded-full" data-testid="reassignment-accept-button">Accetta l'ordine</Button>
            <Button variant="outline" onClick={() => setDialog("decline")} disabled={busy} className="rounded-full" data-testid="reassignment-decline-button">Rifiuta</Button>
          </div>
          <Dialog open={dialog === "decline"} onOpenChange={close}>
            <DialogContent data-testid="reassignment-decline-dialog">
              <DialogHeader>
                <DialogTitle>Rifiutare l'ordine?</DialogTitle>
                <DialogDescription>L'ordine verrà proposto a un altro fornitore e non potrai più accettarlo.</DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button variant="outline" className="rounded-full" onClick={() => setDialog(null)}>Indietro</Button>
                <Button onClick={() => respond(false)} disabled={busy} className="rounded-full bg-red-600 hover:bg-red-700 text-white" data-testid="reassignment-decline-confirm">Rifiuta l'ordine</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      )}

      {isRestOwner && order.status === "open" && (
        <div className="flex justify-end">
          <Button variant="outline" onClick={() => setDialog("cancel")} className="rounded-full border-red-300 text-red-700 hover:bg-red-50" data-testid="cancel-auction-button">
            <Ban className="w-4 h-4 mr-2" />Annulla asta
          </Button>
        </div>
      )}

      {isRestOwner && order.missed_delivery_disputable && (
        <div className="ga-card p-5 border-2 border-red-200 bg-red-50/40" data-testid="missed-delivery-card">
          <div className="text-xs font-mono uppercase tracking-widest text-red-700">Fascia di consegna terminata</div>
          <div className="mt-1 text-sm text-slate-900">Il fornitore non ha ancora segnato l'ordine come consegnato. Se la merce non è arrivata, apri una contestazione: il pagamento resta congelato finché l'amministratore decide.</div>
          <Button variant="outline" onClick={() => setDialog("missed")} className="mt-3 rounded-full border-red-300 text-red-700 hover:bg-red-50" data-testid="missed-delivery-button">
            <Truck className="w-4 h-4 mr-2" />Segnala mancata consegna
          </Button>
        </div>
      )}

      {canWithdraw && (
        <div className="flex justify-end">
          <Button variant="outline" onClick={() => setDialog("withdraw")} className="rounded-full border-red-300 text-red-700 hover:bg-red-50" data-testid="withdraw-order-button">
            <Undo2 className="w-4 h-4 mr-2" />Rinuncia all'ordine
          </Button>
        </div>
      )}

      {withdrawAfterPayment && (
        <div className="text-right text-xs text-slate-500" data-testid="withdraw-after-payment-hint">Per rinunciare dopo il pagamento scrivi all'amministratore</div>
      )}

      <ReasonDialog
        open={dialog === "cancel"} onOpenChange={close} testId="cancel-auction"
        title="Annullare l'asta?"
        description="L'asta si chiude subito e nessuno potrà più offrire. I fornitori che hanno fatto un'offerta riceveranno un avviso."
        label="Motivo (facoltativo)" placeholder="Per esempio: menu cambiato, ordine già coperto" minLength={0}
        confirmLabel="Annulla asta" danger
        onConfirm={(reason) => post("/cancel", { reason: reason || null }, "Asta annullata")}
      />
      <ReasonDialog
        open={dialog === "withdraw"} onOpenChange={close} testId="withdraw-order"
        title="Rinunciare all'ordine?"
        description="Non sarai più il fornitore di questo ordine: l'amministratore lo proporrà alla prossima offerta migliore."
        label="Motivo" placeholder="Spiega perché non puoi consegnare" minLength={10}
        confirmLabel="Rinuncia all'ordine" danger
        onConfirm={(reason) => post("/withdraw", { reason }, "Hai rinunciato all'ordine", true)}
      />
      <ReasonDialog
        open={dialog === "missed"} onOpenChange={close} testId="missed-delivery"
        title="Segnala mancata consegna"
        description="Il fornitore avrà 48 ore per rispondere, poi decide l'amministratore. Se la contestazione è accolta l'ordine viene annullato e il pagamento rimborsato."
        label="Cosa è successo" placeholder="Per esempio: nessuna consegna nella fascia concordata, fornitore non raggiungibile" minLength={10}
        confirmLabel="Apri contestazione" danger
        onConfirm={(reason) => post("/dispute-delivery", { reason, type: "mancata_consegna" }, "Contestazione aperta · pagamento congelato")}
      />
    </>
  );
}
